import { isEmpty } from 'lodash';
import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { ImageStyle } from 'react-native';
import { IconAvatar } from '@shared/components/Icon';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBUserImage } from '@shared/components/image';
import { LoadingIndicator } from '@shared/components/loading';
import { PersonPill, Pill } from '@shared/components/pill';
import { PersonField, usePersons } from '@shared/data/entity/fields/PersonField';
import { useChannelMembersList } from '@shared/scenes/channel/userList/hooks/useChannelMembersList';
import { getComponentLabelWithFallback } from '@shared/scenes/workflows/utils';
import { getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useDebouncedState, useWorkflowChannel } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { EditFieldContainer } from '../EditFieldContainer';
import styles from './PersonEditField.module.css';

const MAX_PEOPLE_TO_SHOW = 10;
const MAX_PILL_WIDTH = 180;

interface Props {
  field: PersonField | null;
  workflowTemplateId: string;
  component: API.PersonComponentFragment;
  updateField: (componentId: string, field: PersonField | null) => void;
  error?: string;
  allowClear?: boolean;
  style?: CSSProperties;
}

export function PersonEditField({
  field,
  workflowTemplateId,
  component,
  updateField,
  error,
  allowClear,
  style,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { persons: selectedPersons, loading: personsLoading } = usePersons(field);

  const onChange = useCallback(
    (changedUsers: User[]) => {
      if (isEmpty(changedUsers)) {
        updateField(component.id, null);
      } else {
        updateField(
          component.id,
          new PersonField(
            changedUsers.map(user => user.id),
            changedUsers,
          ),
        );
      }
    },
    [component.id, updateField],
  );

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const isReadOnly = component.readonly;
  const hasUsers = selectedPersons.length > 0;
  const showClearButton = hasUsers && !isReadOnly && allowClear;
  const anchorName = getAnchorName('person-edit-field', component.id);
  const printContent = selectedPersons.map(u => u.fullName).join(', ') || undefined;

  // Read-only fields show the summary alone, with no menu to open
  const onPress = isReadOnly ? undefined : () => setIsMenuOpen(true);

  return (
    <>
      <PersonSummary
        anchorName={anchorName}
        selectedUsers={selectedPersons}
        component={component}
        onPress={onPress}
        showClearButton={showClearButton}
        onClear={onClear}
        error={error}
        loading={personsLoading}
        style={style}
        printContent={printContent}
      />
      {!isReadOnly && (
        <PersonEditFieldMenu
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          anchorName={anchorName}
          component={component}
          workflowTemplateId={workflowTemplateId}
          selectedPersons={selectedPersons}
          onChange={onChange}
        />
      )}
    </>
  );
}

interface PersonEditFieldMenuProps {
  anchorName: string;
  component: API.PersonComponentFragment;
  workflowTemplateId: string;
  selectedPersons: User[];
  onChange: (users: User[]) => void;
}

/**
 * The picker `PersonEditField` opens from its summary. `Menu.Content` renders nothing while the
 * menu is closed, so everything stateful lives in `PersonMenuContent` inside it: the members query
 * runs only while the menu is open, and each open starts on a fresh search.
 */
function PersonEditFieldMenu({
  isOpen,
  onOpenChange,
  ...contentProps
}: PersonEditFieldMenuProps & {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) {
  const label = getComponentLabelWithFallback(contentProps.component);

  return (
    <Menu
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      anchorName={contentProps.anchorName}
      closeOnSelect={false}
    >
      <Menu.Content mobileSheet editFieldMenu className={styles.personMenu} aria-label={label}>
        <PersonMenuContent label={label} {...contentProps} />
      </Menu.Content>
    </Menu>
  );
}

/**
 * The members of the workflow's channel, searched on the server and loaded 50 at a time.
 *
 * "Everyone" comes first on an empty search once every member has loaded, and selects them all
 * (or clears them when they're all selected). People already selected stay in the list while a
 * search leaves them out, if they match the search text.
 */
function PersonMenuContent({
  label,
  workflowTemplateId,
  selectedPersons,
  onChange,
}: Omit<PersonEditFieldMenuProps, 'anchorName' | 'component'> & { label: string }) {
  const channel = useWorkflowChannel(workflowTemplateId);
  const [search, setSearchText, searchText] = useDebouncedState('', 500);
  const { users, hasMore, loadMore, loading } = useChannelMembersList({
    channelId: channel?.id,
    name: search,
  });
  const selectedIds = new Set(selectedPersons.map(({ id }) => id));
  const query = searchText.toLowerCase();

  // Selected people the current results don't include, filtered by the search text as before
  const availableUsers = useMemo(() => {
    const resultIds = new Set(users.map(({ id }) => id));
    const additionalSelectedUsers = selectedPersons.filter(
      person => !resultIds.has(person.id) && matchesQuery(person, query),
    );
    return [...additionalSelectedUsers, ...users];
  }, [users, selectedPersons, query]);

  const isAllSelected =
    !isEmpty(availableUsers) && availableUsers.every(({ id }) => selectedIds.has(id));
  const showEveryone = !hasMore && isEmpty(query) && !isEmpty(availableUsers);
  const rows: PersonRow[] = [
    ...(showEveryone ? [EVERYONE_ROW] : []),
    ...availableUsers.map(user => ({ kind: 'user' as const, user })),
  ];
  const selectedTitle = `${selectedPersons.length} ${i18n('card.blocks.cardsInput.selected.default')}`;

  const onSelect = (row: PersonRow) => {
    if (row.kind === 'everyone') {
      onChange(isAllSelected ? [] : availableUsers);
      return;
    }
    if (selectedIds.has(row.user.id)) {
      onChange(selectedPersons.filter(({ id }) => id !== row.user.id));
      return;
    }
    onChange([...selectedPersons, row.user]);
  };

  return (
    <>
      <Menu.TitleBar title={label} endContent={<Menu.CloseButton />} mobileOnly />
      <Menu.SearchableOptions
        title={selectedTitle}
        filter={searchText}
        setFilter={setSearchText}
        options={rows}
        loading={loading && isEmpty(users)}
        getItemKey={getPersonRowKey}
        getItemLabel={getPersonRowLabel}
        getItemStartContent={row => <PersonRowImage row={row} />}
        getItemContent={row => <PersonRowLabel row={row} />}
        isItemSelected={row => isPersonRowSelected(row, selectedIds, isAllSelected)}
        onSelect={onSelect}
        multiple
        footer={
          hasMore && <Menu.LoadMore onClick={loadMore} loading={loading && !isEmpty(users)} />
        }
      />
    </>
  );
}

function PersonSummary({
  selectedUsers,
  component,
  anchorName,
  onPress,
  showClearButton,
  onClear,
  error,
  loading,
  style,
  printContent,
}: {
  selectedUsers: User[];
  component: API.PersonComponentFragment;
  anchorName: string;
  onPress?: () => void;
  showClearButton?: boolean;
  onClear?: () => void;
  error?: string;
  loading?: boolean;
  style?: CSSProperties;
  printContent?: string;
}) {
  const hasUsers = !isEmpty(selectedUsers);
  const peopleToShow: User[] = selectedUsers.slice(0, MAX_PEOPLE_TO_SHOW);
  const remainingPersonCount = selectedUsers.length - MAX_PEOPLE_TO_SHOW;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showLabel={true}
      showPlaceholder={!loading && !hasUsers}
      showClearButton={showClearButton}
      onPress={onPress}
      onClear={onClear}
      error={error}
      style={style}
      printContent={printContent}
    >
      {loading && <LoadingIndicator style={{ alignItems: 'flex-start' }} />}
      {!loading && hasUsers && (
        <View className={styles.personPillList}>
          {peopleToShow.map(user => (
            <PersonPill
              key={user.id}
              user={user}
              maxWidth={MAX_PILL_WIDTH}
              showLastName={false}
              pillStyle={personPillStyle}
              textStyle={personPillTextStyle}
            />
          ))}
          {remainingPersonCount > 0 && (
            <Pill
              backgroundColor="white"
              sections={[`+${remainingPersonCount} others`]}
              round
              noBorder
              style={personPillStyle}
              textStyle={personPillTextStyle}
            />
          )}
        </View>
      )}
    </EditFieldContainer>
  );
}

type PersonRow = { kind: 'everyone' } | { kind: 'user'; user: User };

const EVERYONE_ROW: PersonRow = { kind: 'everyone' };

// The avatar size the old sheet's rows used
function PersonRowImage({ row }: { row: PersonRow }) {
  if (row.kind === 'everyone') {
    return <IconAvatar name="avatar-group" size={grid(5)} className={styles.everyoneAvatar} />;
  }
  return <MBUserImage user={row.user} style={personRowAvatarStyle} />;
}

function PersonRowLabel({ row }: { row: PersonRow }) {
  return (
    <View flex centerVertically className={styles.personRowLabel}>
      <Text size="body1" numberOfLines={1}>
        {getPersonRowLabel(row)}
      </Text>
    </View>
  );
}

function getPersonRowKey(row: PersonRow) {
  if (row.kind === 'everyone') {
    return 'everyone';
  }
  return String(row.user.id);
}

function getPersonRowLabel(row: PersonRow) {
  if (row.kind === 'everyone') {
    return i18n('form.placeholder.everyone');
  }
  return row.user.fullName;
}

function isPersonRowSelected(row: PersonRow, selectedIds: Set<number>, isAllSelected: boolean) {
  if (row.kind === 'everyone') {
    return isAllSelected;
  }
  return selectedIds.has(row.user.id);
}

function matchesQuery(user: User, query: string) {
  return user.username.toLowerCase().includes(query) || user.fullName.toLowerCase().includes(query);
}

const personPillStyle: CSSProperties = {
  marginTop: grid(0.25),
  marginBottom: grid(0.25),
  paddingTop: 0,
  paddingBottom: 0,
};

const personPillTextStyle: CSSProperties = { fontSize: 14 };

const personRowAvatarStyle: ImageStyle = {
  width: grid(5),
  height: grid(5),
  borderRadius: grid(2.5),
  overflow: 'hidden',
  marginRight: grid(0.5),
};
